import { useEffect, useRef, useCallback } from 'react';
import { AnalysisPanelState } from '@/types/memento';

interface LocalStorageBackup {
  lastState: AnalysisPanelState;
  lastSavedAt: string;
  currentMementoId: string | null;
  timestamp: number;
}

const STORAGE_KEY = 'currentFocus_backup';
const DEBOUNCE_DELAY = 1000; // 1 second

/**
 * Hook to automatically backup state to localStorage
 * Provides instant recovery on page refresh
 */
export const useLocalStorageBackup = (
  getState: () => AnalysisPanelState | null,
  currentMementoId: string | null,
  enabled: boolean = true
) => {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastBackupRef = useRef<string | null>(null);

  /**
   * Save current state to localStorage
   */
  const saveToLocalStorage = useCallback(() => {
    if (!enabled) return;

    try {
      const state = getState();
      if (!state) return;

      const backup: LocalStorageBackup = {
        lastState: state,
        lastSavedAt: new Date().toISOString(),
        currentMementoId,
        timestamp: Date.now(),
      };

      const serialized = JSON.stringify(backup);
      
      // Only save if state actually changed
      if (serialized !== lastBackupRef.current) {
        localStorage.setItem(STORAGE_KEY, serialized);
        lastBackupRef.current = serialized;
        console.log('[LocalStorageBackup] State backed up to localStorage');
      }
    } catch (error) {
      console.error('[LocalStorageBackup] Failed to save to localStorage:', error);
    }
  }, [getState, currentMementoId, enabled]);

  /**
   * Debounced save - waits for user to stop typing
   */
  const debouncedSave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      saveToLocalStorage();
    }, DEBOUNCE_DELAY);
  }, [saveToLocalStorage]);

  /**
   * Immediate save (for manual saves, stash, etc.)
   */
  const saveImmediately = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    saveToLocalStorage();
  }, [saveToLocalStorage]);

  /**
   * Clear localStorage backup
   */
  const clearBackup = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      lastBackupRef.current = null;
      console.log('[LocalStorageBackup] Backup cleared from localStorage');
    } catch (error) {
      console.error('[LocalStorageBackup] Failed to clear localStorage:', error);
    }
  }, []);

  /**
   * Restore state from localStorage
   */
  const restoreFromLocalStorage = useCallback((): LocalStorageBackup | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const backup: LocalStorageBackup = JSON.parse(stored);
      
      // Check if backup is recent (< 24 hours old)
      const age = Date.now() - backup.timestamp;
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      
      if (age > maxAge) {
        console.log('[LocalStorageBackup] Backup too old, ignoring');
        clearBackup();
        return null;
      }

      console.log('[LocalStorageBackup] Restored from localStorage', {
        age: Math.round(age / 1000 / 60),
        minutes: 'ago',
      });

      return backup;
    } catch (error) {
      console.error('[LocalStorageBackup] Failed to restore from localStorage:', error);
      return null;
    }
  }, [clearBackup]);

  /**
   * Auto-save on state changes (debounced)
   */
  useEffect(() => {
    if (enabled) {
      debouncedSave();
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [enabled, debouncedSave]);

  return {
    saveImmediately,
    clearBackup,
    restoreFromLocalStorage,
  };
};
